/* istanbul ignore file */
import React from 'react';
import {
    IndexRoute,
    Route,
} from 'react-router';
import App from './app/app.jsx';
import { PeoplePathnameSegmentType } from './app/navigation/models';
import { SERVICE_COMPANION_ROUTE_PATHS } from './contentManagement/tabs/constants';
import {
    getReactPluginInstance,
    isAnalyticsAvailable,
    trackPageView,
    withAnalytics,
} from './tools/analytics/index.js';

const AnalyticsReactPluginInstance = getReactPluginInstance();

// HOC: With Page Tracking
const withPageTracking = (component) => (props) => {
    const {
        // eslint-disable-next-line react/prop-types
        location: {
            // eslint-disable-next-line react/prop-types
            pathname,
        },
    } = props;

    /**
     * we don't currently have any requirements on what data to track, so just
     * passing null for state
     */
    trackPageView(pathname, null);

    const WrappedComponent = withAnalytics(AnalyticsReactPluginInstance, component);

    // eslint-disable-next-line react/jsx-filename-extension, react/jsx-props-no-spreading
    return <WrappedComponent {...props} />;
};

// HOC: Get Component
const getComponent = ({ callback, promise }) => {
    promise.then((module) => {
        if (isAnalyticsAvailable()) {
            return callback(null, withPageTracking(module.default));
        }

        return callback(null, module.default);
    });
};

/**
 * Main Route Tree for HC Admin
 * IMPORTANT: To help readability and maintainability, please try to keep this file organized.
 * In general, try to keep things in an order that roughly corresponds with HC Admin application
 * navigation.
 */
export default (
    <Route
        component={App}
        path="/"
    >
        {/* Welcome (Home Page) */}
        <IndexRoute
            getComponent={(location, callback) => getComponent({
                callback,
                promise: import('./welcome/welcome.jsx'),
            })}
        />

        {/* About (unauthenticated page that shows the component versions) */}
        <Route
            getComponent={(location, callback) => getComponent({
                callback,
                promise: import('./app/about'),
            })}
            path="/about"
        />

        {/* My Account */}
        <Route path="/my-account">
            <Route
                getComponent={(location, callback) => getComponent({
                    callback,
                    promise: import('./myAccount/preferences'),
                })}
                path="preferences"
            />
            <Route
                getComponent={(location, callback) => getComponent({
                    callback,
                    promise: import('./myAccount/changePassword/changePassword.jsx'),
                })}
                path="change-password"
            />
        </Route>

        {/* START: People */}
        <Route path={`/${PeoplePathnameSegmentType.People}`}>
            <Route path={PeoplePathnameSegmentType.SearchOrAdd}>
                <IndexRoute
                    getComponent={(location, callback) => getComponent({
                        callback,
                        promise: import('./people/search/peopleSearch.jsx'),
                    })}
                />
                <Route path={PeoplePathnameSegmentType.Record}>
                    <Route
                        getComponent={(location, callback) => getComponent({
                            callback,
                            promise: import('./people/record/record'),
                        })}
                        path={`:personId(${PeoplePathnameSegmentType.Note}/:noteId)`}
                    />
                    <Route
                        getComponent={(location, callback) => getComponent({
                            callback,
                            promise: import('./js/components/Person/Manager/PersonRecord.react.js'),
                        })}
                        path={`:personId/${PeoplePathnameSegmentType.Edit}`}
                    />
                </Route>
                <Route
                    getComponent={(location, callback) => getComponent({
                        callback,
                        promise: import('./js/components/Person/Manager/PersonPhoto.react.js'),
                    })}
                    path="photo/:personId"
                />
            </Route>

            <Route
                getComponent={(location, callback) => getComponent({
                    callback,
                    promise: import('./people/record/printPersonRecordPage'),
                })}
                path="print/:personId"
            />

            <Route
                getComponent={(location, callback) => getComponent({
                    callback,
                    promise: import('./auditLog/globalPersonAuditLog.js'),
                })}
                path="audit-log"
            />

            <Route path="settings">
                {/* Note Categories */}
                <Route path="note-categories">
                    <IndexRoute
                        getComponent={(location, callback) => getComponent({
                            callback,
                            promise: import('./js/components/Security/SecurityNoteCategorySearch.react.js'),
                        })}
                    />
                    <Route
                        getComponent={(location, callback) => getComponent({
                            callback,
                            promise: import('./js/components/Security/SecurityNoteCategoryAdd.react.js'),
                        })}
                        path="add"
                    />
                    <Route
                        getComponent={(location, callback) => getComponent({
                            callback,
                            promise: import('./js/components/Security/SecurityNoteCategoryEdit.react.js'),
                        })}
                        path="edit/:noteCategoryId"
                    />
                    <Route
                        getComponent={(location, callback) => getComponent({
                            callback,
                            promise: import('./js/components/Security/SecurityNoteCategoryView.react.js'),
                        })}
                        path="view/:noteCategoryId"
                    />
                </Route>
            </Route>
        </Route>
        {/* END: People */}

        {/* START: Content Management ('Mobile Engagement') */}
        <Route path="/content-management">
            <IndexRoute
                getComponent={(location, callback) => getComponent({
                    callback,
                    promise: import('./contentManagement/serviceCompanion.jsx'),
                })}
            />
            <Route path="service-companion">
                <IndexRoute
                    getComponent={(location, callback) => getComponent({
                        callback,
                        promise: import('./contentManagement/serviceCompanion.jsx'),
                    })}
                />
                <Route
                    getComponent={(location, callback) => getComponent({
                        callback,
                        promise: import('./contentManagement/tabs/serviceCompanionOverview'),
                    })}
                    path={`:id/${SERVICE_COMPANION_ROUTE_PATHS.overview}`}
                />
                <Route
                    getComponent={(location, callback) => getComponent({
                        callback,
                        promise: import('./contentManagement/tabs/serviceCompanionOverview'),
                    })}
                    path={`:id/${SERVICE_COMPANION_ROUTE_PATHS.overview}/edit`}
                    props={{ isEdit: true }}
                />
                <Route
                    getComponent={(location, callback) => getComponent({
                        callback,
                        promise: import('./contentManagement/tabs/serviceCompanionLocations'),
                    })}
                    path={`:id/${SERVICE_COMPANION_ROUTE_PATHS.locations}`}
                />
            </Route>
            <Route path="digital-program">
                <IndexRoute
                    getComponent={(location, callback) => getComponent({
                        callback,
                        promise: import('./digitalProgram/digitalPrograms'),
                    })}
                />
                <Route
                    getComponent={(location, callback) => getComponent({
                        callback,
                        promise: import('./digitalProgram/builder/digitalProgramBuilderPage'),
                    })}
                    path=":id/builder"
                />
                <Route
                    getComponent={(location, callback) => getComponent({
                        callback,
                        promise: import('./digitalProgram/digitalProgramDetails'),
                    })}
                    path=":id/details"
                />
            </Route>
            <Route path="digital-program-rock">
                <IndexRoute
                    getComponent={(location, callback) => getComponent({
                        callback,
                        promise: import('./digitalProgramRock/digitalPrograms'),
                    })}
                />
                <Route
                    getComponent={(location, callback) => getComponent({
                        callback,
                        promise: import('./digitalProgramRock/builder/digitalProgramBuilderPage'),
                    })}
                    path=":id/builder"
                />
                <Route
                    getComponent={(location, callback) => getComponent({
                        callback,
                        promise: import('./digitalProgramRock/digitalProgramDetails'),
                    })}
                    path=":id/details"
                />
            </Route>
            <Route
                getComponent={(location, callback) => getComponent({
                    callback,
                    promise: import('./contentManagement/serviceSeriesBuilder.jsx'),
                })}
                path="service-series-builder"
            />
        </Route>
        {/* END: Content Management ('Mobile Engagement') */}

        {/* START: System */}
        <Route path="/system">
            {/* Platform Settings */}
            <Route
                getComponent={(location, callback) => getComponent({
                    callback,
                    promise: import('./system/platformSettings/platformSettings.jsx'),
                })}
                path="platform-settings/general"
            />
            <Route
                getComponent={(location, callback) => getComponent({
                    callback,
                    promise: import('./system/platformSettings/platformSettings.jsx'),
                })}
                path="platform-settings/data-covenant"
            />

            {/* Campus Settings */}
            <Route
                getComponent={(location, callback) => getComponent({
                    callback,
                    promise: import('./js/components/ChurchStructure/ChurchStructure.react.js'),
                })}
                isTeam={false}
                path="campus-settings"
            />

            {/* Users */}
            <Route path="users">
                <IndexRoute
                    getComponent={(location, callback) => getComponent({
                        callback,
                        promise: import('./js/components/User/UserSearch.jsx'),
                    })}
                />
                <Route
                    getComponent={(location, callback) => getComponent({
                        callback,
                        promise: import('./js/components/User/UserView.jsx'),
                    })}
                    path="view/:userId"
                />
                <Route
                    getComponent={(location, callback) => getComponent({
                        callback,
                        promise: import('./js/components/User/UserEdit.jsx'),
                    })}
                    path="edit/:userId"
                />
            </Route>

            {/* Teams */}
            <Route
                getComponent={(location, callback) => getComponent({
                    callback,
                    promise: import('./js/components/ChurchStructure/ChurchStructure.react.js'),
                })}
                isTeam
                path="teams"
            />

            {/* Security & Permissions */}
            <Route path="security-roles">
                <IndexRoute
                    getComponent={(location, callback) => getComponent({
                        callback,
                        promise: import('./js/components/Security/SecurityRoleSearch.react.js'),
                    })}
                />
                <Route
                    getComponent={(location, callback) => getComponent({
                        callback,
                        promise: import('./js/components/Security/SecurityRoleAdd.react.js'),
                    })}
                    path="add"
                />
                <Route
                    getComponent={(location, callback) => getComponent({
                        callback,
                        promise: import('./js/components/Security/SecurityRoleEdit.react.js'),
                    })}
                    path="edit/:securityRoleId"
                />
                <Route
                    getComponent={(location, callback) => getComponent({
                        callback,
                        promise: import('./js/components/Security/SecurityRoleView.react.js'),
                    })}
                    path="view/:securityRoleId"
                />
            </Route>

            <Route
                getComponent={(location, callback) => getComponent({
                    callback,
                    promise: import('./js/components/Security/PermissionList.jsx'),
                })}
                path="permissions"
            />
        </Route>
        {/* END: System */}

        {/* START: Help */}
        <Route path="help">
            <IndexRoute
                getComponent={(location, callback) => getComponent({
                    callback,
                    promise: import('./help/help.jsx'),
                })}
            />
            <Route
                getComponent={(location, callback) => getComponent({
                    callback,
                    promise: import('./help/helpArticle.jsx'),
                })}
                path="article/:contentId(/:versionId)"
            />
        </Route>
        {/* END: Help */}

        {/* START: Public-facing pages that don't require authentication */}
        {/* SMS Terms & Conditions and Consent Pages */}
        <Route
            getComponent={(location, callback) => getComponent({
                callback,
                promise: import('./smsConsent/smsConsent.jsx'),
            })}
            path="sms-consent(/:token)"
        />
        <Route
            getComponent={(location, callback) => getComponent({
                callback,
                promise: import('./smsConsent/smsTermsAndConditions.jsx'),
            })}
            path="sms-terms-and-conditions"
        />

        {/* Published Connection Forms */}
        <Route
            getComponent={(location, callback) => getComponent({
                callback,
                promise: import('./connectionForms/public/connectionFormPage'),
            })}
            path="public/connection-form/:connectionFormTemplateId/:connectionFormTemplateSlug"
        />

        {/* START: Public Giving-related Pages */}
        {
            /*
                NOTE: The Giving Checkout (formerly 'Simple Giving') flow, Public Designation Details, Public
                Campaign Details and Manage Saved Payment Methods pages were retired under AB#112440, which trimmed
                the public Giving surface down to Giving History and Campaign Commitment (and took Stripe with it).
            */
        }
        {/* Giving History Page */}
        {
            /*
                NOTE: May or may not be needed.
                Depends how we want to deal with Giving History in mobile app.
                For web, we likely we will use a widget that is embedded on saddleback.com.
            */
        }
        <Route
            getComponent={(location, callback) => getComponent({
                callback,
                promise: import('./giving/public/givingHistory'),
            })}
            path="/public/giving-history"
        />

        {/* Public Campaign Commitment Only Test Page */}
        <Route
            getComponent={(location, callback) => getComponent({
                callback,
                promise: import('./giving/public/campaignCommitment/campaignCommitmentTestPage'),
            })}
            path="/public/campaign-commitment/:campaignId"
        />

        {/* END: Public Giving-related Pages */}

        {/* Public Digital Program */}
        <Route
            getComponent={(location, callback) => getComponent({
                callback,
                promise: import('./digitalProgram/builder/public/digitalProgramPreviewPage'),
            })}
            path="/public/digital-program/campus/:id"
        />

        <Route
            getComponent={(location, callback) => getComponent({
                callback,
                promise: import('./digitalProgram/details/public/digitalProgramTalkItOverPage'),
            })}
            path="/public/digital-program/talkitover/:id"
        />

        {/* Public Digital Program Rock */}
        <Route
            getComponent={(location, callback) => getComponent({
                callback,
                promise: import('./digitalProgramRock/builder/public/digitalProgramPreviewPage'),
            })}
            path="/public/digital-program-rock/campus/:id"
        />

        <Route
            getComponent={(location, callback) => getComponent({
                callback,
                promise: import('./digitalProgramRock/details/public/digitalProgramTalkItOverPage'),
            })}
            path="/public/digital-program-rock/talkitover/:id"
        />

        {/* END: Public-facing pages that don't require authentication */}

        {/* Media Manager Login Redirect Page */}
        <Route
            getComponent={(location, callback) => getComponent({
                callback,
                promise: import('./app/mediaManagerLoginRedirect'),
            })}
            path="/media-manager-login-redirect"
        />

        {/* START: Error Pages */}
        <Route
            getComponent={(location, callback) => getComponent({
                callback,
                promise: import('./js/components/Error/403.react.js'),
            })}
            path="/403"
        />
        <Route
            getComponent={(location, callback) => getComponent({
                callback,
                promise: import('./js/components/Error/404.react.js'),
            })}
            path="*"
        />
        {/* END: Error Pages */}
    </Route>
);
